import { VoidCallback, VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import AutosizeInput from 'react-input-autosize';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { promptName } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { DropButton } from '../../ui/DropButton';
import { PopperInner, PopperMount } from '../canvas/ui/PopperElements';
import { Colors, Float, Prompt } from './../../ui';

const XAutosizeInput = AutosizeInput as any;

const Frame = styled.div`
    display: flex;
    align-items: center;
    margin: 0 2.5rem;

    ${DropButton} {
        color: ${Colors.primaryText};
        padding: .375rem .75rem;
        margin-left: -.750rem;

        input {
            min-width: 3rem;
        }
    }
`;

const Title = styled.div<{ link?: boolean }>`
    display: inline-block;
    color: ${Colors.primaryText};
    font-size: 1.25rem;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 20rem;

    ${p => !p.link ? '' : css`
        color: ${Colors.text};

        &:hover {
            color: ${Colors.primaryText};
        }
    `}
`;

const Divide = styled.div`
    display: inline-block;
    position: relative;
    width: 2px;
    height: 0.75rem;
    margin: 0 1em;
    
    &:after {
        content: '';
        position: absolute;
        top: -.5em;
        bottom: -.5em;
        left: 0;
        right: 0;
        background: #e9ebee;
    }
`;

const Menu: LFC<{ label: string, open: boolean, setOpen: VoidCallback1<boolean> }> = ({ label, open, setOpen, children }) => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-start',
    });

    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);
    const pn = promptName();

    return <>
        <DropButton role="button" aria-label={label} className={`ignore-${pn}`} rounding="full" ref={setRefElmt} active={open} onClick={onToggle}>
            <Title>{label}</Title>
            <Icon size="1.2" src="deqo://icons/hero/chevron-down" />
        </DropButton>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float shadow="lg" inline>{children}</Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </>;
};

const ItemFrame = styled.div`
    display: inline-flex;
    align-items: center;

    a {
        display: contents;
    }
`;

const MenuWithOpen: LFC<{ label: string }> = ({ label, children }) => {
    const [open, setOpen] = useState(false);
    return <Menu label={label} open={open} setOpen={setOpen}>
        {children}
    </Menu>;
};

const Item: LFC<{ label: string, param: any }> = ({ label, param }) =>
    <>
        {param && typeof (param) === 'string' &&
            <Link to={param}>
                <Title link>{label}</Title>
            </Link>
        }
        {param && typeof (param) === 'function' &&
            <MenuWithOpen label={label}>{param()}</MenuWithOpen>
        }
        {!param &&
            <Title>{label}</Title>
        }
    </>;


export type PageCrumbItem = readonly (readonly [string, (() => JSX.Element) | string | null])[];
const Base: LFC<{ items?: PageCrumbItem }> = ({ items, children }) =>
    <Frame>
        {items && items.filter(x => !!x).map(([label, param], i) =>
            <ItemFrame key={label}>
                {i > 0 && <Divide />}
                <Item {...{ label, param }} />
            </ItemFrame>
        )}
        {children && <>
            {items && items.length && <Divide />}
            <ItemFrame>{children}</ItemFrame>
        </>}
    </Frame>;

type InputProps = { initialValue: string, onApply: VoidCallback1<string>, onCancel: VoidCallback };
const Input: LFC<InputProps> = ({ initialValue, onApply, onCancel }) => {

    const [value, setValue] = useState(initialValue);
    const onPromptAction = useCallback((confirm: boolean) => {
        if (confirm) onApply(value);
        else onCancel();
    }, [value]);

    return <Prompt onAction={onPromptAction} clickOffIsPositive>
        <DropButton active rounding="full">
            <XAutosizeInput autoFocus type="text" autoComplete="false" value={value} maxLength={64} style={{ fontSize: '1.25rem' }}
                onChange={(e: any) => setValue(e.target.value)} onFocus={(e: any) => e.target.select()} />
        </DropButton>
    </Prompt>;
};

export const Crumbs = Object.assign(Base, {
    Title,
    Item,
    Divide,
    Menu,
    Input,
});
